import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

import { PHASES, RAIL_SECTIONS } from './navigation';
import { wf } from './tokens';

export type RailProgressState = 'notStarted' | 'inProgress' | 'completed';
type RailState = RailProgressState | 'locked';

interface IntakeShellProps {
  organizationName: string;
  currentPageKey: string;
  /** Page keys the customer's config does not require. */
  notNeededKeys: Set<string>;
  /** True until the config gate is answered; everything past K1/F1 stays locked. */
  gateOpen: boolean;
  percentComplete: number;
  onNavigate: (pageKey: string) => void;
  /** Suggestions waiting on each page, so the rail can show where to look. */
  pendingByPage?: Record<string, { total: number; unconfirmed: number }>;
  /** Durable field completion for each generated page. */
  progressByPage?: Record<string, RailProgressState>;
  children: ReactNode;
}

function LockIcon() {
  return (
    <svg width="11" height="12" viewBox="0 0 12 14" fill="none" aria-hidden="true">
      <rect x="1.5" y="6" width="9" height="6.5" rx="1.5" stroke={wf.faint} strokeWidth="1.4" />
      <path d="M3.5 6V4.2a2.5 2.5 0 0 1 5 0V6" stroke={wf.faint} strokeWidth="1.4" />
    </svg>
  );
}

function Dot({ isCurrent, state }: { isCurrent: boolean; state: RailState }) {
  if (state === 'locked') return <LockIcon />;
  if (state === 'completed') {
    return (
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M3 8.5 6.5 12 13 4.5"
          stroke={wf.green}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  const filled = state === 'inProgress' || isCurrent;
  return (
    <Box
      component="span"
      sx={{
        width: '9px',
        height: '9px',
        borderRadius: '50%',
        display: 'block',
        background: filled ? wf.blue : 'transparent',
        border: filled ? 'none' : `1.4px solid ${wf.faint}`,
      }}
    />
  );
}

export function IntakeShell({
  organizationName,
  currentPageKey,
  notNeededKeys,
  gateOpen,
  percentComplete,
  onNavigate,
  pendingByPage = {},
  progressByPage = {},
  children,
}: IntakeShellProps) {
  const visibleSections = RAIL_SECTIONS.map(section => ({
    ...section,
    items: section.items.filter(item => !notNeededKeys.has(item.pageKey)),
  })).filter(section => section.items.length > 0);
  const visiblePhases = PHASES.filter(phase =>
    visibleSections.some(section => section.phase === phase),
  );
  const currentPhase =
    visibleSections.find(section => section.items.some(item => item.pageKey === currentPageKey))
      ?.phase ?? PHASES[0];

  const stateFor = (pageKey: string): RailState => {
    if (!gateOpen && !['K1', 'F1'].includes(pageKey)) return 'locked';
    return progressByPage[pageKey] ?? 'notStarted';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        color: wf.ink,
        background: wf.surface,
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          borderBottom: `1px solid ${wf.line}`,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>
          JotPsych Intake{' '}
          <Box component="span" sx={{ color: wf.muted, fontWeight: 400 }}>
            · {organizationName}
          </Box>
        </Typography>
      </Box>

      <Box sx={{ borderBottom: `1px solid ${wf.line}`, padding: '8px 16px 0', flexShrink: 0 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          <Box sx={{ display: 'flex', gap: '18px' }}>
            {visiblePhases.map(phase => {
              const isCurrent = phase === currentPhase;
              return (
                <Box
                  key={phase}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '11.5px',
                    paddingBottom: '8px',
                    color: isCurrent ? wf.blue : wf.muted,
                    fontWeight: isCurrent ? 700 : 400,
                    boxShadow: isCurrent ? `inset 0 -2px 0 ${wf.blue}` : 'none',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: '9px',
                      height: '9px',
                      borderRadius: '50%',
                      background: isCurrent ? wf.blue : 'transparent',
                      border: isCurrent ? 'none' : `1.4px solid ${wf.faint}`,
                    }}
                  />
                  {phase}
                </Box>
              );
            })}
          </Box>
          <Box sx={{ fontSize: '11.5px', color: wf.muted, paddingBottom: '8px' }}>
            {percentComplete}% complete
          </Box>
        </Box>
        <Box sx={{ height: '4px', background: wf.track, margin: '0 -16px' }}>
          <Box sx={{ height: '100%', width: `${percentComplete}%`, background: wf.blue }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flex: '1 1 auto', minHeight: 0 }}>
        <Box
          component="nav"
          aria-label="Intake sections"
          sx={{
            width: '230px',
            flexShrink: 0,
            borderRight: `1px solid ${wf.line}`,
            padding: '12px 10px',
            overflowY: 'auto',
            background: wf.surfaceAlt,
          }}
        >
          {visibleSections.map(section => {
            const completedItems = section.items.filter(
              item => stateFor(item.pageKey) === 'completed',
            );
            const sectionPercentComplete = Math.round(
              (completedItems.length / section.items.length) * 100,
            );

            return (
              <Box key={section.key}>
                <Box
                  sx={{
                    fontSize: '10px',
                    letterSpacing: '.08em',
                    color: wf.muted,
                    textTransform: 'uppercase',
                    margin: '12px 6px 4px',
                  }}
                >
                  {section.label}
                </Box>
                <Box
                  role="progressbar"
                  aria-label={`${section.label} completion`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={sectionPercentComplete}
                  sx={{
                    height: '3px',
                    borderRadius: '2px',
                    background: '#e3e5e9',
                    margin: '2px 6px 6px',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${sectionPercentComplete}%`,
                      borderRadius: '2px',
                      background: wf.blue,
                      transition: 'width 180ms ease-out',
                    }}
                  />
                </Box>
                {section.items.map(item => {
                  const state = stateFor(item.pageKey);
                  const isCurrent = item.pageKey === currentPageKey;
                  const interactive = state !== 'locked';
                  return (
                    <Box
                      key={item.pageKey}
                      role={interactive ? 'button' : undefined}
                      aria-current={isCurrent ? 'step' : undefined}
                      data-progress-state={state}
                      tabIndex={interactive ? 0 : undefined}
                      onClick={() => interactive && onNavigate(item.pageKey)}
                      onKeyDown={event => {
                        if (interactive && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault();
                          onNavigate(item.pageKey);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '5px 8px',
                        borderRadius: '6px',
                        fontSize: '12.5px',
                        cursor: interactive ? 'pointer' : 'default',
                        background: isCurrent ? wf.blueSoft : 'transparent',
                        fontWeight: isCurrent ? 600 : 400,
                        color: isCurrent
                          ? wf.blue
                          : state === 'notStarted'
                            ? wf.muted
                            : state === 'inProgress' || state === 'completed'
                              ? wf.ink
                              : wf.faint,
                        '&:hover': interactive
                          ? { background: isCurrent ? wf.blueSoft : '#eef0f3' }
                          : {},
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: '14px',
                          height: '14px',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Dot isCurrent={isCurrent} state={state} />
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          flexGrow: 1,
                          minWidth: 0,
                        }}
                      >
                        {item.label}
                      </Box>
                      {(pendingByPage[item.pageKey]?.unconfirmed ?? 0) > 0 && (
                        <Box
                          component="span"
                          aria-label={`${pendingByPage[item.pageKey]?.unconfirmed} values to review`}
                          sx={{
                            flexShrink: 0,
                            minWidth: '17px',
                            height: '17px',
                            padding: '0 5px',
                            borderRadius: '9px',
                            background: '#fdf6e3',
                            border: '1px solid #8a5a00',
                            color: '#8a5a00',
                            fontSize: '10px',
                            fontWeight: 800,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {pendingByPage[item.pageKey]?.unconfirmed}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>

        <Box
          component="main"
          sx={{
            flex: '1 1 auto',
            padding: '20px 26px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
